import { z } from "zod";

/**
 * Brand profile request shapes — docs/03-API-SPEC.md §4: "GET/POST/PATCH /brand-profiles |
 * superadmin". One profile is shared platform-wide by default (`isDefault: true`, seeded as
 * "Seplat / NNPC JV"); a form can point at a different one via `Form.brandProfileId`.
 */

const colorsSchema = z.object({
  primary: z.string(),
  primaryContrast: z.string(),
  accent: z.string(),
  surface: z.string(),
  background: z.string(),
  text: z.string(),
  border: z.string(),
  error: z.string(),
  success: z.string(),
});

export const brandProfileCreateRequestSchema = z.object({
  name: z.string().min(1).max(200),
  logoLayout: z
    .enum(["side_by_side", "stacked", "primary_only", "secondary_only"])
    .default("side_by_side"),
  colors: colorsSchema,
  headingFont: z.string().min(1).max(100).default("Inter"),
  bodyFont: z.string().min(1).max(100).default("Inter"),
  footerText: z.string().max(500).nullable().optional(),
  privacyUrl: z.string().url().nullable().optional(),
  dpoContact: z.string().max(300).nullable().optional(),
  isDefault: z.boolean().default(false),
});

/** Every field optional — a PATCH updates only what it names, including clearing a logo to null. */
export const brandProfileUpdateRequestSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  primaryLogoId: z.string().nullable().optional(),
  secondaryLogoId: z.string().nullable().optional(),
  faviconId: z.string().nullable().optional(),
  logoLayout: z.enum(["side_by_side", "stacked", "primary_only", "secondary_only"]).optional(),
  colors: colorsSchema.partial().optional(),
  headingFont: z.string().min(1).max(100).optional(),
  bodyFont: z.string().min(1).max(100).optional(),
  footerText: z.string().max(500).nullable().optional(),
  privacyUrl: z.string().url().nullable().optional(),
  dpoContact: z.string().max(300).nullable().optional(),
  isDefault: z.boolean().optional(),
});

export type BrandProfileCreateRequest = z.infer<typeof brandProfileCreateRequestSchema>;
export type BrandProfileUpdateRequest = z.infer<typeof brandProfileUpdateRequestSchema>;
